/**
 * HistoricalBalanceOverrideDialog (T3-075 / T3-076 / T3-080)  — the admin "Amend Historical Contract" dialog.
 * Admin-only, tenant-toggle gated. Lets an administrator amend a Historical (pre-go-live) contract.
 *
 * Two modes (branch on contract.is_multi_investor):
 *  - SINGLE-investor / single-pool: amend the allocatable rollover ceiling (+ external reference), with a
 *    Current / Proposed / Difference display (true ceiling fetched from GET /:id/allocatable-ceiling since
 *    it can't be computed client-side). Backed by POST /:id/historical-override-balance.
 *  - MULTI-investor: the contract-level ceiling is NOT the binding rollover cap — each investor's
 *    capital slice is. So the modal lists each investor (current → new capital, no interest/WHT columns)
 *    and posts POST /:id/historical-amend-investor per changed investor. (T3-080)
 *
 * A MANDATORY reason is recorded in the audit trail in both modes.
 * (Filename/component name kept to avoid import churn; user-facing text says "Amend".)
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { apiGet, apiPost, extractApiData, extractApiError } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import CurrencyInput, { formatWithSpaces } from './CurrencyInput';
import logger from '../../utils/logger';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {object} props.contract - the Historical contract ({ id, reference, user_reference, currency_code, allocatable_override, product_type, is_multi_investor })
 * @param {Function} props.onClose
 * @param {Function} props.onSaved - called after a successful amendment (to refresh)
 */
const HistoricalBalanceOverrideDialog = ({ open, contract, onClose, onSaved }) => {
  const isMulti = !!(contract && contract.is_multi_investor);

  const [amount, setAmount] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ceiling, setCeiling] = useState(null);        // effective current ceiling (honours override)
  const [computedPool, setComputedPool] = useState(null); // pool ignoring override (what "clear" yields)
  const [loadingCeiling, setLoadingCeiling] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);        // non-blocking "ref already exists" note
  const dupDebounce = useRef(null);
  // Multi-investor mode: per-investor rows [{ investor_id, investor_name, current, next }]
  const [investorRows, setInvestorRows] = useState([]);
  const [loadingInvestors, setLoadingInvestors] = useState(false);

  // On open: seed fields; single-investor fetches the ceiling, multi-investor fetches investors.
  useEffect(() => {
    if (!open || !contract) return undefined;
    setExternalRef(contract.user_reference || '');
    setReason('');
    setError(null);
    setDupWarn(null);
    let cancelled = false;

    if (isMulti) {
      setInvestorRows([]);
      setLoadingInvestors(true);
      apiGet(`/contracts/${contract.id}/investors`)
        .then((res) => {
          if (cancelled) return;
          const rows = (extractApiData(res) || []).map((inv) => ({
            investor_id: inv.investor_id,
            investor_name: inv.investor_name,
            current: Number(inv.capital_amount) || 0,
            next: String(Number(inv.capital_amount) || 0),
          }));
          setInvestorRows(rows);
        })
        .catch((err) => { if (!cancelled) logger.error('Error loading investors', { message: err.message }); })
        .finally(() => { if (!cancelled) setLoadingInvestors(false); });
      return () => { cancelled = true; };
    }

    // Single-investor: seed the ceiling amount + fetch the true current ceiling.
    setAmount(contract.allocatable_override != null ? String(contract.allocatable_override) : '');
    setCeiling(null);
    setComputedPool(null);
    setLoadingCeiling(true);
    apiGet(`/contracts/${contract.id}/allocatable-ceiling`)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data || {};
        setCeiling(d.ceiling != null ? Number(d.ceiling) : null);
        setComputedPool(d.computed_pool != null ? Number(d.computed_pool) : null);
      })
      .catch((err) => { if (!cancelled) logger.error('Error loading allocatable ceiling', { message: err.message }); })
      .finally(() => { if (!cancelled) setLoadingCeiling(false); });
    return () => { cancelled = true; };
  }, [open, contract, isMulti]);

  // Non-blocking duplicate-reference check when the external ref is changed to something else.
  useEffect(() => {
    if (dupDebounce.current) clearTimeout(dupDebounce.current);
    const ref = (externalRef || '').trim();
    const orig = (contract?.user_reference || '').trim();
    if (!contract || ref.length < 2 || ref.toLowerCase() === orig.toLowerCase()) {
      setDupWarn(null);
      return undefined;
    }
    dupDebounce.current = setTimeout(async () => {
      try {
        const res = await apiGet('/contracts/historical-search?q=' + encodeURIComponent(ref));
        const rows = (extractApiData(res) || []).filter((o) => {
          if (o.id === contract.id) return false;
          if ((o.user_reference || '').trim().toLowerCase() !== ref.toLowerCase()) return false;
          if (contract.product_type && o.product_type && o.product_type !== contract.product_type) return false;
          return true;
        });
        setDupWarn(rows.length > 0 ? rows.length : null);
      } catch { setDupWarn(null); }
    }, 350);
    return () => { if (dupDebounce.current) clearTimeout(dupDebounce.current); };
  }, [externalRef, contract]);

  const reasonValid = reason.trim().length > 0;
  const amtTrim = String(amount).trim();
  const amtValid = amtTrim === '' || (Number.isFinite(parseFloat(amtTrim)) && parseFloat(amtTrim) >= 0);

  if (!contract) return null;
  const ccy = contract.currency_code || '';
  const fmt = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `${ccy} ${formatNumber(Number(v), 2)}`);

  // --- multi-investor derived values ---
  const rowValid = (r) => Number.isFinite(parseFloat(r.next)) && parseFloat(r.next) > 0;
  const allRowsValid = investorRows.length > 0 && investorRows.every(rowValid);
  const changedRows = investorRows.filter((r) => rowValid(r) && parseFloat(r.next) !== r.current);
  const newTotal = investorRows.reduce((s, r) => s + (parseFloat(r.next) || 0), 0);

  // --- single-investor derived values ---
  const proposed = amtTrim === '' ? computedPool : parseFloat(amtTrim);
  const difference = (ceiling != null && proposed != null && Number.isFinite(proposed)) ? proposed - ceiling : null;

  const setRowNext = (idx, v) => setInvestorRows((prev) => prev.map((r, i) => (i === idx ? { ...r, next: v } : r)));

  const canApply = isMulti
    ? (reasonValid && allRowsValid && changedRows.length > 0 && !saving)
    : (reasonValid && amtValid && !saving);

  const handleSubmit = async () => {
    if (!canApply) return;
    setSaving(true);
    setError(null);
    try {
      if (isMulti) {
        // One POST per changed investor (shared reason).
        for (const r of changedRows) {
          // eslint-disable-next-line no-await-in-loop
          await apiPost(`/contracts/${contract.id}/historical-amend-investor`, {
            investor_id: r.investor_id,
            capital_amount: parseFloat(r.next),
            reason: reason.trim(),
          });
        }
      } else {
        await apiPost(`/contracts/${contract.id}/historical-override-balance`, {
          allocatable_override: amtTrim === '' ? null : parseFloat(amtTrim),
          user_reference: (externalRef || '').trim() || null,
          reason: reason.trim(),
        });
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      logger.error('Error amending historical contract', { message: err.message, status: err.response?.status });
      setError(extractApiError(err, 'Failed to amend historical contract'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => { if (!saving) onClose(); }} maxWidth={isMulti ? 'md' : 'sm'} fullWidth>
      <DialogTitle>Amend Historical Contract</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {isMulti ? (
            <>Amend the per-investor capital of multi-investor historical contract{' '}
              <strong>{contract.reference}</strong>
              {contract.user_reference ? ` (${contract.user_reference})` : ''}. Each investor&apos;s capital
              is the amount other contracts can roll from that investor&apos;s slice. A reason is required
              and is recorded in the audit trail.</>
          ) : (
            <>Amend the allocatable rollover ceiling and/or external reference of historical contract{' '}
              <strong>{contract.reference}</strong>
              {contract.user_reference ? ` (${contract.user_reference})` : ''}. Leave the amount blank to
              clear the amendment and revert to the computed pool. A reason is required and is recorded in
              the audit trail.</>
          )}
        </DialogContentText>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {isMulti ? (
          /* ---- Multi-investor: per-investor capital table ---- */
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Per-Investor Capital</Typography>
            <TableContainer sx={{ mb: 2, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>Investor</TableCell>
                    <TableCell align="right">Current Capital</TableCell>
                    <TableCell align="right" sx={{ minWidth: 180 }}>New Capital</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {investorRows.map((r, idx) => (
                    <TableRow key={r.investor_id}>
                      <TableCell>{r.investor_name}</TableCell>
                      <TableCell align="right">{ccy} {formatWithSpaces(r.current)}</TableCell>
                      <TableCell align="right">
                        <CurrencyInput
                          size="small"
                          value={r.next}
                          onChange={(v) => setRowNext(idx, v)}
                          sx={{ '& input': { textAlign: 'right' } }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell><Typography variant="body2" fontWeight="bold">Total</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {ccy} {formatWithSpaces(investorRows.reduce((s, r) => s + r.current, 0))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">{ccy} {formatWithSpaces(newTotal)}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {loadingInvestors && <Typography variant="caption" color="textSecondary">Loading investors…</Typography>}
          </>
        ) : (
          /* ---- Single-investor: ceiling + Current/Proposed/Difference + external ref ---- */
          <>
            <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="textSecondary">Current ceiling</Typography>
                <Typography variant="body2" fontWeight="bold">{loadingCeiling ? '…' : fmt(ceiling)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="textSecondary">
                  Proposed{amtTrim === '' ? ' (cleared → computed pool)' : ''}
                </Typography>
                <Typography variant="body2" fontWeight="bold">{fmt(proposed)}</Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="textSecondary">Difference</Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={difference == null ? 'textSecondary' : difference > 0 ? 'success.main' : difference < 0 ? 'error' : 'textSecondary'}
                >
                  {difference == null ? '—' : `${difference > 0 ? '+' : ''}${ccy} ${formatNumber(difference, 2)}`}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                label={`Allocatable Ceiling${ccy ? ` (${ccy})` : ''}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                InputLabelProps={{ shrink: true }}
                error={amtTrim !== '' && !amtValid}
                helperText="Blank = clear the amendment (revert to the computed pool). Must be >= 0."
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                label="External Reference"
                value={externalRef}
                onChange={(e) => setExternalRef(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="The external (prior-system) reference. Change to correct a take-on typo."
              />
              {dupWarn != null && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  A historical contract with this reference already exists
                  {dupWarn > 1 ? ` (${dupWarn} matches)` : ''} — you may want to <em>merge</em> instead of
                  creating a matching reference.
                </Alert>
              )}
            </Box>
          </>
        )}

        <TextField
          label="Reason *"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          error={reason.length > 0 && !reasonValid}
          helperText={'Required. Recorded in the audit trail (e.g. "take-on error"). Do not include client PII.'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canApply}>
          {saving ? 'Saving...' : 'Apply Amendment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoricalBalanceOverrideDialog;
